import React, { useState, useEffect } from 'react';
import { getAppStoreVersions, createAppStoreVersion, updateAppStoreVersion } from '../../services/api';

function VersionManager({ app, currentVersion, onVersionChange, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newVersionString, setNewVersionString] = useState('');
  const [formData, setFormData] = useState({
    copyright: '',
    releaseType: 'MANUAL'
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (app) {
      loadVersions();
    }
  }, [app]);

  useEffect(() => {
    if (currentVersion) {
      setFormData({
        copyright: currentVersion.attributes.copyright || '',
        releaseType: currentVersion.attributes.releaseType || 'MANUAL'
      });
    }
  }, [currentVersion]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const response = await getAppStoreVersions(app.id);
      if (response.data.success) {
        setVersions(response.data.versions);
      }
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!newVersionString) {
      alert('Please enter a version number');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await createAppStoreVersion(app.id, {
        versionString: newVersionString,
        platform: 'IOS'
      });

      if (response.data.success) {
        alert('Version created successfully!');
        setShowCreateNew(false);
        setNewVersionString('');
        await loadVersions();
        onVersionChange(response.data.version);
        onUpdate();
      } else {
        // Handle API error response
        const errorData = response.data.error;
        if (errorData && errorData.details && errorData.details.length > 0) {
          setError(errorData.details[0].detail);
        } else {
          setError(errorData?.message || 'Failed to create version');
        }
      }
    } catch (err) {
      const errorData = err.response?.data?.error;
      if (errorData && errorData.details && errorData.details.length > 0) {
        setError(errorData.details[0].detail);
      } else {
        setError(errorData?.message || err.message || 'An unknown error occurred');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentVersion) {
      alert('No version selected');
      return;
    }

    setSaving(true);
    try {
      await updateAppStoreVersion(currentVersion.id, formData);
      alert('Version updated successfully!');
      onUpdate();
    } catch (err) {
      alert('Failed to update version: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="form-group">
        <label className="form-label">Current Version</label>
        <select
          className="form-select"
          value={currentVersion?.id || ''}
          onChange={(e) => {
            const version = versions.find(v => v.id === e.target.value);
            onVersionChange(version);
          }}
        >
          {versions.map(version => (
            <option key={version.id} value={version.id}>
              Version {version.attributes.versionString} - {version.attributes.appStoreState}
            </option>
          ))}
        </select>
      </div>

      {!showCreateNew ? (
        <button
          className="btn btn-secondary"
          onClick={() => {
            setShowCreateNew(true);
            setError(null);
          }}
          style={{ marginBottom: '20px' }}
        >
          + Create New Version
        </button>
      ) : (
        <div className="form-card" style={{ marginBottom: '20px' }}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '16px' }}>
              <span className="alert-icon">⚠️</span>
              <span><strong>Error:</strong> {error}</span>
            </div>
          )}
          <h4 style={{ marginBottom: '16px' }}>Create New Version</h4>
          <div className="form-group">
            <label className="form-label">Version Number</label>
            <input
              type="text"
              className="form-input"
              placeholder="1.0.0"
              value={newVersionString}
              onChange={(e) => setNewVersionString(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary"
              onClick={handleCreateVersion}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowCreateNew(false);
                setNewVersionString('');
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {currentVersion && (
        <>
          <div className="info-card" style={{ marginBottom: '20px' }}>
            <h4 className="text-primary" style={{ marginBottom: '8px' }}>
              Version {currentVersion.attributes.versionString}
            </h4>
            <p style={{ fontSize: '14px', marginBottom: '4px' }}>
              <strong>State:</strong> {currentVersion.attributes.appStoreState}
            </p>
            <p style={{ fontSize: '14px', marginBottom: 0 }}>
              <strong>Platform:</strong> {currentVersion.attributes.platform}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Copyright</label>
            <input
              type="text"
              className="form-input"
              placeholder="© 2025 Your Company Name"
              value={formData.copyright}
              onChange={(e) => setFormData({ ...formData, copyright: e.target.value })}
            />
            <small className="text-secondary">
              The person or entity that owns the exclusive rights to your app
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Release Type</label>
            <select
              className="form-select"
              value={formData.releaseType}
              onChange={(e) => setFormData({ ...formData, releaseType: e.target.value })}
            >
              <option value="MANUAL">Manual release</option>
              <option value="AFTER_APPROVAL">Automatically release after approval</option>
              <option value="SCHEDULED">Schedule for a specific date</option>
            </select>
            <small className="text-secondary">
              Choose when to release your app after it's approved
            </small>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Version Details'}
          </button>
        </>
      )}
    </div>
  );
}

export default VersionManager;
